import React, { useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Menu, MenuItem, Button, Divider } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClientSearch from "../../components/ClientSearch";
import { clientSearchSecondaryLabel } from "../../models/Client";
import { tl, t } from "../../components/translate";
import DebouncedTextField from "../../components/DebouncedTextField";
import { Client } from "../../interfaces/ClientInterface";

export enum ASSESSMENT_SEARCH_BY {
  CLIENT = "client",
  ASSESSMENT_ID = "assessmentId",
  DIAGNOSIS = "diagnosis"
}

export type AssessmentSearchByType =
  (typeof ASSESSMENT_SEARCH_BY)[keyof typeof ASSESSMENT_SEARCH_BY];

interface Props {
  searchBy: AssessmentSearchByType;
  onSearchByChange: (v: AssessmentSearchByType) => void;
  searchValue: string;
  onSearchValueChange: (v: string) => void;
  selectedClient: Client;
  onClientChange: (v: Client | null) => void;
}

const SearchInput = ({
  focused,
  searchValue,
  onSearchValueChange
}: {
  focused: boolean;
  searchValue: string;
  onSearchValueChange: (v: string) => void;
}) => (
  <DebouncedTextField
    debounceAt={700}
    sx={{ marginLeft: focused ? "35px" : 0 }}
    placeholder={focused ? "Search..." : ""}
    size="small"
    fullWidth
    value={searchValue}
    onChange={(e) => onSearchValueChange(e.target.value)}
    slotProps={{
      input: { disableUnderline: true }
    }}
  />
);

const AssessmentListSearch = ({
  searchBy,
  onSearchByChange,
  searchValue,
  onSearchValueChange,
  selectedClient,
  onClientChange
}: Props): React.ReactNode => {
  const toggleRef = useRef(null);
  const [focused, setFocused] = React.useState(true);

  const searchComponentMapper: Record<
    AssessmentSearchByType,
    { label: React.ReactNode; component: React.ReactNode }
  > = {
    [ASSESSMENT_SEARCH_BY.CLIENT]: {
      label: tl("billing.client"),
      component: (
        <ClientSearch
          autoFocus
          style={{ paddingLeft: focused ? "35px" : "" }}
          client={selectedClient}
          setClient={onClientChange}
          margin="none"
          placeholder={focused ? "Search..." : ""}
          secondaryText={[
            { type: clientSearchSecondaryLabel.CUSTOMER_NUMBER },
            {
              type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER,
              label: t("client.externalIdentifier.short")
            }
          ]}
        />
      )
    },
    [ASSESSMENT_SEARCH_BY.ASSESSMENT_ID]: {
      label: "OPD ID",
      component: (
        <SearchInput
          focused={focused}
          searchValue={searchValue}
          onSearchValueChange={onSearchValueChange}
        />
      )
    },
    [ASSESSMENT_SEARCH_BY.DIAGNOSIS]: {
      label: "Diagnosis",
      component: (
        <SearchInput
          focused={focused}
          searchValue={searchValue}
          onSearchValueChange={onSearchValueChange}
        />
      )
    }
  };

  const menuItems = Object.keys(searchComponentMapper) as AssessmentSearchByType[];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const toggleMenu = (
    <div>
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ paddingLeft: 0, whiteSpace: "pre" }}
      >
        {searchComponentMapper[searchBy].label} <ExpandMoreIcon />
      </Button>
      <Menu
        ref={toggleRef}
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item}
            onClick={() => {
              setAnchorEl(null);
              onSearchByChange(item);
            }}
          >
            {searchComponentMapper[item].label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  return (
    <Box
      onFocus={() => setFocused(true)}
      sx={{
        position: "relative",
        borderRadius: "5px",
        width: focused ? "280px" : "30px",
        height: "35px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        transition: "width .5s, background 1s ease",
        ...(focused && {
          backgroundColor: "#ececec",
          width: { xs: "240px", sm: "280px" }
        })
      }}
    >
      <SearchIcon sx={{ position: "absolute", width: "35px" }} />
      <Box style={{ marginTop: "5px" }} width="220px">
        {searchComponentMapper[searchBy].component}
      </Box>
      <Divider
        sx={{
          height: 20,
          marginRight: focused ? "15px" : "-300px"
        }}
        orientation="vertical"
      />
      <Box sx={{ marginRight: focused ? 0 : "-300px" }}>{toggleMenu}</Box>
    </Box>
  );
};

export default AssessmentListSearch;
